import { default as _log } from '@dw/loglevel';
import EventEmitter from "@dreamworld/event-emitter";
import { PouchDB } from '@dw/pouchdb-esm/pouchdb-find.js';
// import PouchDB from 'pouchdb';

//lodash
import filter from 'lodash-es/filter';

/**
 * Represents a local(PouchDB) database.
 * 
 * - Dispatches `changes` event whenever any change is found in the local database (Pouchdb).
 *   Event value will be the Array of documents; It will contain design-docs too, 
 *   so user need to be careful when using it.
 * - Local database name will be the combination of `couchdb.name` and `dbName`. 
 *   Format: `${couchdb.name}-${dbName}`.
 */
export default class LocalDatabase extends EventEmitter {
  constructor(dbName, couchdb) {
    super();
    this.dbName = dbName;
    this.couchdb = couchdb;
    this._localDB = new PouchDB(`${couchdb.name}-${dbName}`);
    this._designDocs = {}; //key=design-doc-id, value = doc.
    this.logPrefix = `dreamDB.couchDB.${this.couchdb.name}.db.${this.dbName}.localDB`;
    // Set logger
    this.log = _log.getLogger(`dreamdb.localDatabase.${this.couchdb.name}.${this.dbName}`);
    this._populateDesignDocs();
    this._startChangesFeed();
  }

  /**
   * Read-only property. Returns value of `_designDocs`
   */
  get designDocs() {
    return this._designDocs;
  }

  /**
   * Read-only property. Returns local database instance
   */
  get localDB() {
    return this._localDB;
  }

  /**
   * Writes documents to the local database.
   * @param {*} docs Single document or Array of documents
   * @returns A Promise which is resolved to the bulk-docs response.
   */
  async save(docs) {
    return this._bulkWrite(docs, true);
  }

  /**
   * Reads documents from the local database.
   * 
   * @param {*} docIds Either a single documentId, or the array of documentIds.
   * @returns Promise which is resolved to the Array of Row. Row object is the same as
   *   represented by the `rows` in the 
   *   [Pouchdbs' batch-fetch response](https://pouchdb.com/api.html#batch_fetch).
   *   Note: In case argument is single docId, response is Row object (not an Array of Rows).
   */
  async fetch(docIds) {
    return new Promise(async (resolve, reject) => {

      //If single document id then creates array
      let aDocIds = typeof docIds === 'string' ? [docIds] : docIds;

      try {
        let options = {
          include_docs: true,
          keys: aDocIds
        };

        //Reads documents
        let result = await this._localDB.allDocs(options);
        let docs = typeof docIds === 'string' ? result.rows[0] : result.rows;
        resolve(docs);
      } catch (err) {
        this.log.error(`${this.logPrefix}.fetch(): fetch failed`, err);
        reject(err);
      }
    });
  }

  /**
   * Reads documents from the local database, includes revision history
   * @param {Array} docs Array of object, In single object contains id field.
   * @returns {Promise} returns promise which resolved with fetched docs.
   */
  async fetchWithRevs(docs) {
    return new Promise(async (resolve, reject) => {
      try {
        let options = {
          docs,
          revs: true
        };

        //Reads documents
        let result = await this._localDB.bulkGet(options);

        let resultDocs = result.results.map((result) => {
          return result.docs[0].ok;
        });

        resolve(resultDocs);

      } catch (err) {
        this.log.error(`${this.logPrefix}.fetchWithRevs(): fetch with revs failed`, err);
        reject(err);
      }
    });
  }

  /**
   * An internal method. Must not be used by the users.
   * Used to save document-updates received from the couchdb to the local database.
   * So, it writes document using bulk-docs, where `new-edits=false` as it 
   * needs to preseve the same revision information sent by the couchdb.
   * 
   * @param {*} docs 
   * @returns A Promise which is resolved to the bulk-docs response.
   */
  async saveRemoteDocsToLocal(docs) {
    return this._bulkWrite(docs, false);
  }

  /**
   * It writes docs in local database.
   * @param {Array} docs Array of documents
   * @param {Boolean} newEdits true of false
   * @requires {Promise} returns promise
   */
  async _bulkWrite(docs, newEdits) {
    return this._localDB.bulkDocs(docs, {
      new_edits: newEdits
    });
  }

  /**
   * It populated `this._designDocs` by reading data from the local pouchdb.
   * Invoked only once when the instance of this is created.
   */
  async _populateDesignDocs() {
    try {
      let result = await this._localDB.allDocs({
        include_docs: true,
        startkey: '_design/',
        endkey: '_design/\ufff0'
      });

      result.rows.forEach((ddoc) => {
        this._designDocs[ddoc.id] = ddoc;
      });

      //It dispatches `ddocs-populated` event
      this.emit('ddocs-populated');

      this.log.info(`${this.logPrefix}._populateDesignDocs(): Design docs populated`, this._designDocs);

    } catch (err) {
      this.log.error(`${this.logPrefix}._populateDesignDocs(): Failed to populated design docs`, err);
    }
  }


  /**
   * Updates `this._designDocs` if any design-docs have been newly created, updated or deleted.
   * @param {Array} docs - Array of docs which have been updated (received through changes feed)
   */
  _updateDesignDocs(docs) {
    let ddocs = filter(docs, (doc) => {
      return doc.id.startsWith("_design/");
    });

    if (!ddocs.length) {
      return;
    }

    ddocs.forEach((ddoc) => {
      if (ddoc.deleted) {
        delete this._designDocs[ddoc.id];
        return;
      }

      this._designDocs[ddoc.id] = ddoc;
    });

    this.log.info(`${this.logPrefix}._updateDesignDocs(): design docs updated`, this._designDocs);
  }

  /**
   * An internal changens feed handler. This is invoked when new changes are found, 
   * and once the promise returned by this function is resolved then only `changes` 
   * event is dispatched.
   * @param {Array} docs Array of documents which have been created/updated/deleted.
   */
  async _onChanges(docs) {
    return new Promise((resolve) => {
      this._updateDesignDocs(docs);
      resolve(docs);
    });
  }

  /**
   * starts listening changes feed (since=now) and dispatches `changes`
   * event when new docs are found.
   */
  async _startChangesFeed() {
    let self = this;

    try {
      let pendingChanges = undefined;
      let changesHandler = (change) => {
        if (pendingChanges) {
          pendingChanges.push(change);
          return;
        }

        pendingChanges = [change];
        window.setTimeout(() => {
          self._onChanges(pendingChanges).then((changes) => {
            this.log.debug(`${this.logPrefix}._startChangesFeed(): local db changed =`, changes);
            this.emit('changes', changes);
            pendingChanges = undefined;
          });
        });
      };

      await this._localDB.changes({
        since: 'now',
        live: true,
        include_docs: true
      }).on('change', changesHandler)
        .on('error', (err) => {
          this.log.error(`${this.logPrefix}._startChangesFeed(): Error while listening changes`, err);
        });
    } catch (err) {
      this.log.error(`${this.logPrefix}._startChangesFeed(): Error when start to listening changes`, err);
    }
  }

  /**
   * It sets that local database is synced with the server atleast once.
   * It creates local document(_local/synced-with-server) into local database with `synced: true`.
   * @return {Promise}
   */
  async setSyncedWithServer() {
    if(this._syncInProgressPromise) {
      return this._syncInProgressPromise;
    }
    
    this._syncInProgressPromise = this._setSyncDatabase();
    return this._syncInProgressPromise;
  }

  async _setSyncDatabase() {
    let isSynced = await this._fetchSyncDetails();

    if(isSynced) {
      return;
    }

    return this._localDB.put({
      _id: '_local/synced-with-server',
      synced: true
    });
  }

  /**
   * It fetches local document(_local/synced-with-server)
   * @return {Promise} Resolve with true/false.
   */
  async _fetchSyncDetails() {
    try {
      let doc = await this._localDB.get('_local/synced-with-server');
      return doc.synced;
    } catch (err) {
      if(err.status === 404) {
        return false;
      }
    }
  }

  /**
   * It's used to check whether the database is synced with the server OR not.
   */
  async isSyncedWithServer() {
    return await this._fetchSyncDetails();
  }

  /**
   * 
   * @param {*} ddocId Id of the design doc. e.g. `_design/validate-contact`.
   * @returns a Promise which is resolved when the requested design doc
   * becomes available in the local database. Promise may never get resolved
   * if document isn't available ever.
   */
  waitForDesignDoc(ddocId) {
    return new Promise((resolve) => {
      if (this._designDocs[ddocId]) {
        resolve(this._designDocs[ddocId]);
      }

      let changesHandler = () => {
        if (this._designDocs[ddocId]) {
          resolve(this._designDocs[ddocId]);
          this.off('changes', changesHandler);
        }
      };

      this.on('changes', changesHandler);
      this.on('ddocs-populated', changesHandler);
      this.log.debug(`${this.logPrefix}.waitForDesignDoc: waiting for design doc: ddocId=${ddocId}`);
    });
  }

  //It will close the local database's connection.
  disconnect() {
    this.localDB.close().then(() => {
      this.log.info(`${this.logPrefix}.disconnect(): local database is disconnected.`)
    });
  }

  //It will Delete the local database.
  destroy() {
    return this.localDB.destroy().catch((err) => {
      this.log.error(`${this.logPrefix}.destroy(): local database destroy failed`, err);
    });
  }
}